/**
 * Checking an events XML with the vendored converter, which is the reader VTS uses.
 *
 * The wasm's validate call takes a whole bundle with a required `main` IDL. An
 * event mapping edit has no IDL of its own to send (the adapter's IDL is on the
 * server), so the request carries a stand-in `main` that declares nothing and keeps
 * only the findings about the events file, the ones whose source is `EventsXml`.
 * The stand-in is not empty: an empty `main` stops the converter at its own blob
 * check, before it reads the events. A non-empty one gets findings of its own, which
 * are dropped here, and the converter goes on to the events. See the test beside this
 * file, which runs the real wasm.
 *
 * `mce_events_parser` is the parser in both places. The converter reads an events
 * file with `parse_with_diagnostics`, VTS with `FromStr`: they accept the same files
 * except that the converter also refuses a duplicate event id, a namespace
 * declaration and a file over its size limits. It never accepts what VTS refuses.
 *
 * A proper events-only call in the converter's ABI would make the stand-in
 * unnecessary. That is a change to the wasm and its schemas, which this does not make.
 */
import { DiagnosticCode } from "../../flatbuffers/generated/mce/diagnostic-code";
import { IdlResultT } from "../../flatbuffers/generated/mce/idl-result";
import { ProductType } from "../../flatbuffers/generated/mce/product-type";
import { Severity } from "../../flatbuffers/generated/mce/severity";
import { SourceFile } from "../../flatbuffers/generated/mce/source-file";
import { validateMceIdl } from "./client";
import { MceIdlSources, asString } from "./messages";
import { MceSaveFailure } from "./saveUpload";

/** A stand-in IDL: one comment line, so it is not empty and declares no interface. */
export const EVENTS_ONLY_MAIN: Uint8Array = new TextEncoder().encode("// events file only\n");

/** One problem the converter found in the events file. */
export interface EventMappingFinding {
  code: DiagnosticCode;
  line: number;
  column: number;
  message: string;
}

type Validate = (product: ProductType | null, sources: MceIdlSources) => Promise<IdlResultT>;

/**
 * The errors in an events XML, in the order the converter found them. Empty text has
 * nothing to check and gives none: clearing the field is allowed.
 *
 * Rejects when the converter cannot run (its worker did not start, or its files do not
 * match the pinned ones); that is not a verdict on the file.
 */
export async function checkEventMapping(
  xml: string,
  validate: Validate = validateMceIdl,
): Promise<EventMappingFinding[]> {
  if (xml.trim() === "") return [];
  const result = await validate(null, {
    main: { name: "main.idl", bytes: EVENTS_ONLY_MAIN },
    includes: [],
    eventsXml: { name: "events.xml", bytes: new TextEncoder().encode(xml) },
  });
  return result.diagnostics
    .filter((one) => one.source === SourceFile.EventsXml && one.severity === Severity.Error)
    .map((one) => ({
      code: one.code,
      line: one.line,
      column: one.column,
      message: asString(one.message),
    }));
}

/** A finding as one line: where it is, then what it says. */
export function describeFinding(finding: EventMappingFinding): string {
  return `line ${finding.line}, column ${finding.column}: ${finding.message}`;
}

const CHECK_UNAVAILABLE =
  "The Event Mapping Data could not be checked right now, so the adapter was not saved. Try again. If this continues, click Get log and send the file to technical support.";

/**
 * The reason a save must not go ahead with this events XML, or null when it may. A
 * converter that cannot run is a reason too: saving unchecked XML that VTS cannot read
 * silently turns event reporting off for the adapter.
 */
export async function refuseEventMapping(
  xml: string,
  validate: Validate = validateMceIdl,
): Promise<MceSaveFailure | null> {
  try {
    return eventMappingFailure(await checkEventMapping(xml, validate));
  } catch (error) {
    return { message: CHECK_UNAVAILABLE, detail: `event mapping check: ${String(error)}` };
  }
}

/** One plain message for a file with errors, and every finding for Get log. Null when there are none. */
export function eventMappingFailure(findings: EventMappingFinding[]): MceSaveFailure | null {
  if (findings.length === 0) return null;
  const noun = findings.length === 1 ? "error" : "errors";
  return {
    message: `The Event Mapping Data has ${findings.length} ${noun}, the first at ${describeFinding(findings[0])}. The adapter was not saved. Click Get log for the full list.`,
    detail: `event mapping check: ${findings.length} ${noun}\n${findings.map(describeFinding).join("\n")}`,
  };
}
